import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { Loader2, Calendar, Clock, TrendingUp, ArrowLeft, Bell, Check } from "lucide-react";

const PrelimsRevisionSchedulerPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [topic, setTopic] = useState("");
  const [result, setResult] = useState(null);
  const [remindersSet, setRemindersSet] = useState({});

  const calculateDate = (daysFromNow) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleSetReminder = (cycleIdx, cycle) => {
    const date = new Date();
    date.setDate(date.getDate() + cycle.day - 1);
    const dateStr = date.toISOString().split('T')[0];
    
    const eventTitle = `CLARIAS: Prelims Revision - ${result.topic} (Cycle ${cycleIdx + 1})`;
    const eventDetails = `Revise ${cycle.questions} questions on ${result.topic}`;
    
    // Create Google Calendar event URL
    const startDate = dateStr.replace(/-/g, '');
    const endDate = startDate;
    const googleCalendarUrl = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventTitle)}&dates=${startDate}/${endDate}&details=${encodeURIComponent(eventDetails)}&sf=true`;
    
    // Open Google Calendar in new tab
    window.open(googleCalendarUrl, '_blank');
    
    // Mark reminder as set
    setRemindersSet(prev => ({ ...prev, [cycleIdx]: true }));
    
    toast({
      description: `📅 Reminder set for Cycle ${cycleIdx + 1} - ${calculateDate(cycle.day - 1)}`
    });
  };

  const handleGenerateSchedule = async () => {
    if (!topic.trim()) {
      toast({
        variant: "destructive",
        description: "Please enter a topic"
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(
        `${API_BASE}/prelims/revision/schedule/${encodeURIComponent(topic)}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResult(response.data);
      toast({
        description: `Revision schedule generated for ${topic}!`
      });
    } catch (err) {
      console.error("Error:", err);
      toast({
        variant: "destructive",
        description: err.response?.data?.detail || "Failed to generate revision schedule"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      {/* Back Button */}
      <Button variant="outline" onClick={() => navigate('/prelims')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Prelims Hub
      </Button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Revision Scheduler</h1>
          <p className="text-sm text-slate-600">
            Spaced repetition schedule for optimal retention
          </p>
        </div>
        <Badge className="bg-green-500">
          🔓 FREE
        </Badge>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900 mb-1">
                Based on Cognitive Science:
              </p>
              <p className="text-sm text-slate-700">
                Spaced repetition at optimal intervals (1, 3, 7, 14, 30 days) has been proven 
                to increase long-term retention by up to 200%. This schedule is personalized for UPSC preparation.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration Card */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div>
            <Label htmlFor="topic">Topic to Schedule</Label>
            <Input
              id="topic"
              placeholder="e.g., Fundamental Rights, Fiscal Policy, Climate Change"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              disabled={loading}
            />
          </div>

          <Button
            onClick={handleGenerateSchedule}
            disabled={loading}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating Schedule...
              </>
            ) : (
              <>
                <Calendar className="w-4 h-4 mr-2" />
                Generate Revision Schedule
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Topic Header */}
          <Card className="bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                Revision Schedule for: {result.topic}
              </h3>
              <div className="flex items-center gap-4">
                <Badge className="bg-cyan-600">
                  {result.schedule?.length || 0} Revision Cycles
                </Badge>
                <Badge variant="secondary">
                  Forgetting Probability: {result.forgetting_probability}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Schedule Timeline */}
          {result.schedule && result.schedule.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-slate-900">
                    Spaced Repetition Timeline
                  </h3>
                </div>
                <div className="space-y-4">
                  {result.schedule.map((cycle, idx) => {
                    const dateStr = cycle.date === "Today" 
                      ? "Today" 
                      : calculateDate(cycle.day - 1);
                    
                    return (
                      <div
                        key={idx}
                        className={`border-l-4 ${
                          idx === 0 ? "border-l-green-500" :
                          idx === 1 ? "border-l-blue-500" :
                          idx === 2 ? "border-l-purple-500" :
                          idx === 3 ? "border-l-orange-500" : "border-l-red-500"
                        } bg-slate-50 rounded-r-lg p-4`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Badge
                                className={
                                  idx === 0 ? "bg-green-600" :
                                  idx === 1 ? "bg-blue-600" :
                                  idx === 2 ? "bg-purple-600" :
                                  idx === 3 ? "bg-orange-600" : "bg-red-600"
                                }
                              >
                                Cycle {idx + 1}
                              </Badge>
                              <span className="text-sm font-semibold text-slate-900">
                                Day {cycle.day}
                              </span>
                              <span className="text-sm text-slate-600">
                                ({dateStr})
                              </span>
                            </div>
                            <p className="text-sm text-slate-700">
                              Revise {cycle.questions} questions on this topic
                            </p>
                          </div>
                          <Button 
                            variant={remindersSet[idx] ? "default" : "outline"} 
                            size="sm"
                            onClick={() => handleSetReminder(idx, cycle)}
                            className={remindersSet[idx] ? "bg-green-600 hover:bg-green-700" : ""}
                          >
                            {remindersSet[idx] ? (
                              <>
                                <Check className="w-4 h-4 mr-1" />
                                Reminder Set
                              </>
                            ) : (
                              <>
                                <Bell className="w-4 h-4 mr-1" />
                                Set Reminder
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Strong & Weak Zones */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {result.strong_zones && result.strong_zones.length > 0 && (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-slate-900">Strong Zones</h3>
                  </div>
                  <ul className="space-y-1">
                    {result.strong_zones.map((zone, idx) => (
                      <li key={idx} className="text-sm text-slate-700 flex items-center gap-2">
                        <span className="text-green-600">✓</span>
                        {zone}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {result.weak_zones && result.weak_zones.length > 0 && (
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-5 h-5 text-red-600 rotate-180" />
                    <h3 className="font-semibold text-slate-900">Weak Zones</h3>
                  </div>
                  <ul className="space-y-1">
                    {result.weak_zones.map((zone, idx) => (
                      <li key={idx} className="text-sm text-slate-700 flex items-center gap-2">
                        <span className="text-red-600">⚠</span>
                        {zone}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Tips */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-amber-900 mb-2">
                💡 Revision Tips
              </h3>
              <ul className="text-sm text-slate-700 space-y-1">
                <li>• Don't skip any cycle - each one is crucial for long-term retention</li>
                <li>• Active recall works better than passive reading</li>
                <li>• Test yourself with practice questions during each cycle</li>
                <li>• Focus more time on weak zones during revisions</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PrelimsRevisionSchedulerPage;
